import { GenderEnum } from '@wonderschool/common-base-types';
import { Input, RadioButtonGroup } from '@wonderschool/common-base-ui';
import dayjs from 'dayjs';
import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormComponentProps } from '../../../common';
import { capitalize } from '../../../helpers/utils';
import { StudentAndParentsType } from '../../../students/types';

type EnrollmentStudentEditFormProps = FormComponentProps<any> & {
  form: UseFormReturn<StudentAndParentsType>;
};

function EnrollmentStudentEditForm({ title, hasTitle = true, form }: EnrollmentStudentEditFormProps) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
  } = form;

  const viewTitle = hasTitle ? t(title || 'students.studentInformationFormTitle') : '';

  const genderItems = [
    { value: GenderEnum.FEMALE, label: t(capitalize(GenderEnum.FEMALE)), name: GenderEnum.FEMALE },
    { value: GenderEnum.MALE, label: t(capitalize(GenderEnum.MALE)), name: GenderEnum.MALE },
    { value: GenderEnum.OTHER, label: t(capitalize(GenderEnum.OTHER)), name: GenderEnum.OTHER },
  ];

  const getGenderValue = (value: any) => genderItems.find((item) => item.value === value?.value);

  return (
    <div className="new-student-form" data-testid="new-student-form">
      {!!viewTitle && (
        <h2 className="mb-4 text-2xl font-bold" data-testid="new-student-form-title">
          {viewTitle}
        </h2>
      )}
      <div className="-mx-2 flex flex-wrap">
        <div className="mb-4 w-full px-2 md:w-1/2">
          <div className={`${errors.student?.firstName ? 'border-red-500' : ''}`} data-testid="error-firstname">
            <Controller
              name="student.firstName"
              control={control}
              rules={{ required: t('common.firstNameRequired') }}
              render={({ field, formState: { errors } }) => (
                <Input
                  {...field}
                  required
                  type="text"
                  placeholder={t('common.firstName')}
                  label={t('common.firstName')}
                  data-testid="first-name"
                  className="w-full rounded border p-2"
                  error={errors.student?.firstName?.message}
                />
              )}
            />
          </div>
        </div>
        <div className="mb-4 w-full px-2 md:w-1/2">
          <div className={`${errors.student?.lastName ? 'border-red-500' : ''}`} data-testid="error-lastname">
            <Controller
              name="student.lastName"
              control={control}
              rules={{ required: t('common.lastNameRequired') }}
              render={({ field, formState: { errors } }) => (
                <Input
                  {...field}
                  required
                  type="text"
                  placeholder={t('common.lastName')}
                  label={t('common.lastName')}
                  data-testid="last-name"
                  className="w-full rounded border p-2"
                  error={errors.student?.lastName?.message}
                />
              )}
            />
          </div>
        </div>
      </div>

      <div className="mb-4">
        <label className="mb-2 block">{t('students.fields.gender')}</label>
        <Controller
          name="student.gender"
          control={control}
          render={({ field }) => (
            <RadioButtonGroup
              {...field}
              value={getGenderValue(field.value)}
              data-testid="checkbox-gender"
              items={genderItems}
              className="flex space-x-4"
            />
          )}
        />
      </div>
      <div className="mb-4 w-full md:w-1/2">
        <div className={`${errors.student?.birthday ? 'border-red-500' : ''}`} data-testid="birthday-field">
          <Controller
            name="student.birthday"
            rules={{
              required: t('enrollments.errors.birthdateRequired'),
              validate: {
                maxDate: (date) => dayjs(date).isBefore(dayjs('2100-12-31')) || t('enrollments.errors.maxDate'),
              },
            }}
            control={control}
            render={({ field, formState: { errors } }) => (
              <Input
                {...field}
                required
                value={field.value ?? ''}
                label={t('students.fields.birthday')}
                type="date"
                inputType="date"
                error={errors.student?.birthday?.message}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
}

export default EnrollmentStudentEditForm;
